import type {
  TrainingModuleCategory,
  TrainingModuleSummary,
  TrainingReorderRow,
  TrainingSection,
  TrainingTopic,
} from "@xop/dashboard-shared";

import { deriveKindFromUrl } from "../VideoFieldsEditor.js";
import { formatDuration } from "../VideoReader.js";

/**
 * Pure logic behind Documentation ▸ Online Training — kept out of the
 * components so it is unit-tested (`trainingLogic.test.ts`).
 */

export { formatDuration };

/** One place for the words: a module is a Service or an Extension. */
export const CATEGORY_LABEL: Record<TrainingModuleCategory, string> = {
  service: "Service",
  extension: "Extension",
};

/**
 * Parse what an author types into the duration box. `m:ss`, `h:mm:ss`, or a
 * plain number of seconds. Empty ⇒ `null` (no duration). Anything else ⇒
 * `undefined`, which the form shows as an error rather than silently saving 0.
 */
export function parseDuration(raw: string): number | null | undefined {
  const s = raw.trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) return Number(s);
  const parts = s.split(":");
  if (parts.length < 2 || parts.length > 3) return undefined;
  if (!parts.every((p) => /^\d+$/.test(p))) return undefined;
  const nums = parts.map(Number);
  // Every field after the first is a 0–59 clock field.
  if (nums.slice(1).some((n) => n > 59)) return undefined;
  return nums.reduce((acc, n) => acc * 60 + n, 0);
}

/** The duration as the box shows it: `m:ss` (or `h:mm:ss`), blank for none. */
export function durationInput(seconds: number | null | undefined): string {
  return seconds && seconds > 0 ? formatDuration(seconds) : "";
}

/** "12 videos · 34 min" for a module card. */
export function moduleStats(m: Pick<TrainingModuleSummary, "topic_count" | "total_duration_seconds">): string {
  const videos = `${m.topic_count} ${m.topic_count === 1 ? "video" : "videos"}`;
  if (!m.total_duration_seconds) return videos;
  const mins = Math.round(m.total_duration_seconds / 60);
  return `${videos} · ${mins < 1 ? "under a minute" : `${mins} min`}`;
}

/** Group modules for the list page — Services first, then Extensions, each by position. */
export function groupModules(modules: TrainingModuleSummary[]): {
  services: TrainingModuleSummary[];
  extensions: TrainingModuleSummary[];
} {
  const byPos = (a: TrainingModuleSummary, b: TrainingModuleSummary): number =>
    a.position - b.position || a.title.localeCompare(b.title);
  return {
    services: modules.filter((m) => m.category === "service").sort(byPos),
    extensions: modules.filter((m) => m.category === "extension").sort(byPos),
  };
}

/** Every topic in menu order — sections, each followed by its lessons. */
export function flattenMenu(sections: TrainingSection[]): TrainingTopic[] {
  const out: TrainingTopic[] = [];
  for (const s of sections) {
    const { lessons, ...section } = s;
    out.push(section);
    out.push(...lessons);
  }
  return out;
}

export function findTopic(sections: TrainingSection[], id: number): TrainingTopic | null {
  return flattenMenu(sections).find((t) => t.id === id) ?? null;
}

/** The reorder body for a menu: EVERY topic, 1-based positions per sibling list. */
export function reorderRows(sections: TrainingSection[]): TrainingReorderRow[] {
  const rows: TrainingReorderRow[] = [];
  sections.forEach((s, i) => {
    rows.push({ id: s.id, parent_id: null, position: i + 1 });
    s.lessons.forEach((l, j) => rows.push({ id: l.id, parent_id: s.id, position: j + 1 }));
  });
  return rows;
}

function swap<T>(list: T[], i: number, j: number): T[] {
  const next = list.slice();
  const a = next[i];
  const b = next[j];
  if (a === undefined || b === undefined) return list;
  next[i] = b;
  next[j] = a;
  return next;
}

/**
 * Move a topic one step up or down among its siblings. A section moves among
 * sections (its lessons travel with it); a lesson moves within its section.
 * Returns the new menu, or `null` when the topic is already at that end (or
 * unknown) — the caller disables the button then.
 */
export function moveTopic(
  sections: TrainingSection[],
  id: number,
  dir: "up" | "down",
): TrainingSection[] | null {
  const delta = dir === "up" ? -1 : 1;
  const si = sections.findIndex((s) => s.id === id);
  if (si >= 0) {
    const to = si + delta;
    if (to < 0 || to >= sections.length) return null;
    return swap(sections, si, to);
  }
  for (let i = 0; i < sections.length; i++) {
    const s = sections[i]!;
    const li = s.lessons.findIndex((l) => l.id === id);
    if (li < 0) continue;
    const to = li + delta;
    if (to < 0 || to >= s.lessons.length) return null;
    const next = sections.slice();
    next[i] = { ...s, lessons: swap(s.lessons, li, to) };
    return next;
  }
  return null;
}

/**
 * How a pasted video link previews. Detection reuses the documentation video
 * editor's `deriveKindFromUrl`; this only adds the embed address, because a
 * training topic's link is stored as typed (portal-api does not normalise it
 * the way it does a video DOCUMENT's).
 *   - YouTube (watch / youtu.be / shorts / live / embed) → youtube.com/embed/<id>
 *   - Vimeo (vimeo.com/<id>, player.vimeo.com/video/<id>) → player.vimeo.com/video/<id>
 *   - a direct video file (.mp4 / .webm / .mov / .m4v / .ogv) → a native <video>
 *   - anything else → no inline preview, just "open the link"
 */
export type VideoPreview =
  | { type: "iframe"; src: string }
  | { type: "video"; src: string }
  | { type: "external"; href: string }
  | { type: "invalid" };

export function previewForLink(raw: string): VideoPreview {
  const url = raw.trim();
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return { type: "invalid" };
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return { type: "invalid" };

  const kind = deriveKindFromUrl(url);
  if (kind === "youtube") {
    const id = youTubeId(parsed);
    return id ? { type: "iframe", src: `https://www.youtube.com/embed/${id}` } : { type: "external", href: url };
  }
  if (kind === "vimeo") {
    const m = parsed.pathname.match(/(?:^|\/)(\d{5,})(?:\/|$)/);
    return m ? { type: "iframe", src: `https://player.vimeo.com/video/${m[1]}` } : { type: "external", href: url };
  }
  if (/\.(mp4|webm|mov|m4v|ogv)$/i.test(parsed.pathname)) return { type: "video", src: url };
  return { type: "external", href: url };
}

function youTubeId(u: URL): string | null {
  const ok = (id: string | null | undefined): string | null => (id && /^[\w-]{6,}$/.test(id) ? id : null);
  if (u.hostname.endsWith("youtu.be")) return ok(u.pathname.split("/")[1]);
  const v = u.searchParams.get("v");
  if (v) return ok(v);
  const m = u.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/?#]+)/);
  return ok(m?.[1]);
}

/** "12.4 MB" for an uploaded file. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
